// src/lib/mindGarden/wordPlot.ts
// 1語の座標を解決する。ロジカル版の実座標があればそれを最優先し、
// 無ければ8カテゴリの代表座標（重心）にフォールバックする＝橋渡し方式の実装本体。
// どちらも無い語（お題名の直接引用・自由記述テキストなど）はnull。
// 2026-07-20作成。

import { LOGICAL_PLOT, type PlotCoord } from "@/constants/logicalPlot";
import { getWordCategory } from "./wordCategories";
import { CATEGORY_CENTROIDS } from "./categoryCentroids";

export interface ResolvedPlot {
  x: number;
  y: number;
  /** "exact" = ロジカル版の実座標そのもの。"category" = カテゴリ代表座標での代用 */
  source: "exact" | "category";
}

export function getWordPlot(word: string): ResolvedPlot | null {
  const exact: PlotCoord | undefined = LOGICAL_PLOT[word];
  if (exact) return { x: exact.x, y: exact.y, source: "exact" };

  const category = getWordCategory(word);
  if (!category) return null;

  const centroid = CATEGORY_CENTROIDS[category];
  if (centroid.sampleCount === 0) return null; // 場面・情景／感嘆はロジカル版に代表座標が無い

  return { x: centroid.x, y: centroid.y, source: "category" };
}

/**
 * 単語列（1セッション分）の重心座標を計算する。
 * 座標が解決できない語（お題名等）は無視して、解決できた語だけで平均を取る。
 * 1つも解決できない場合はnullを返す（呼び出し側で「未分類セッション」として扱う想定）。
 */
export function getSessionCentroid(words: string[]): { x: number; y: number; resolvedCount: number; totalCount: number } | null {
  const resolved = words.map(getWordPlot).filter((p): p is ResolvedPlot => p !== null);
  if (resolved.length === 0) return null;

  const x = resolved.reduce((sum, p) => sum + p.x, 0) / resolved.length;
  const y = resolved.reduce((sum, p) => sum + p.y, 0) / resolved.length;
  return { x, y, resolvedCount: resolved.length, totalCount: words.length };
}
